import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import classnames from 'classnames'
import { motion } from 'framer-motion'

import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import FormattedPrice from 'shared/components/common/price/FormattedPrice'

import CartIcon from 'public/components/default_template/online_ordering/cart/CartIcon'
import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import { getCartQuantity } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useCart } from 'public/components/online_ordering/CartContext'

const StickyOrderFooter = () => {
  const { cart } = useCart()
  const cartModalContext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const numberOfItems = getCartQuantity(cart)
  const prevQty = usePrevious(numberOfItems)
  const [showQty, setShowQty] = useState(false)
  const discountValue = useMemo(
    () => cart?.order?.discountsTotal,
    [cart?.order?.discountsTotal]
  )

  const bounce = useCallback(() => {
    const bounceElements = [
      document.getElementById('footer-element'),
      document.getElementById('qty-element')
    ]
    bounceElements.forEach((el) => {
      el?.classList.add('bounce')
      el?.addEventListener('animationend', () => el.classList.remove('bounce'))
    })
  }, [])

  useEffect(() => {
    const qty = numberOfItems
    if (qty !== prevQty) {
      // On initial slide-in (going from 0 items to >0 items), show qty after a short delay and trigger bounce
      if (qty > 0 && prevQty === 0) {
        setShowQty(false)
        setTimeout(() => {
          setShowQty(true)
          bounce()
        }, 1000)
      } else {
        bounce()
      }
    }
  }, [prevQty, numberOfItems, bounce])

  if (!cart?.order || !numberOfItems || !cart.order.totalV2) {
    return null
  }

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeOut'
      }}
      className='stickyFooterContainer'
    >
      <button
        className='footer'
        id='footer-element'
        data-testid='sticky-order-footer'
        onClick={() => cartModalContext?.open()}
      >
        <div
          className={classnames(
            'content',
            discountValue ? 'lessPadding' : undefined
          )}
          data-testid='footer-content'
        >
          {discountValue ? (
            <span>
              View order — You’re saving{' '}
              <span className='amount'>
                <FormattedPrice value={discountValue} trailingZeros={false} />
              </span>
            </span>
          ) : (
            <span>
              View order —{' '}
              <span className='amount'>
                <FormattedPrice value={cart?.order?.totalV2} />
              </span>
            </span>
          )}
        </div>
        <div className='cart'>
          <CartIcon />
          {showQty && (
            <div className='qty' id='qty-element'>
              {numberOfItems}
            </div>
          )}
        </div>
      </button>
    </motion.div>
  )
}

export default StickyOrderFooter
